import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { User, Check, X, AlertCircle, Loader2, Camera, Upload, ArrowRight, SkipForward } from "lucide-react";
import { dbGet, uploadFile } from "../../config/firebase";

// Default avatar path
const DEFAULT_AVATAR = '/assets/avatar.svg';

interface UsernameSetupModalProps {
  isOpen: boolean;
  onSubmit: (username: string, photoFile?: File | null) => Promise<void>;
  suggestedUsername?: string;
  suggestedPhotoURL?: string | null;
}

type SetupStep = 'username' | 'photo';

export const UsernameSetupModal: React.FC<UsernameSetupModalProps> = ({
  isOpen,
  onSubmit,
  suggestedUsername = "",
  suggestedPhotoURL = null,
}) => {
  const [step, setStep] = useState<SetupStep>('username');
  const [username, setUsername] = useState(suggestedUsername);
  const [error, setError] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Photo step state
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(suggestedPhotoURL);
  const [useGooglePhoto, setUseGooglePhoto] = useState(!!suggestedPhotoURL);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validate username format
  const validateUsername = (value: string): string | null => {
    if (!value.trim()) {
      return "Username is required";
    }
    if (value.length < 3) {
      return "Username must be at least 3 characters";
    }
    if (value.length > 20) {
      return "Username must be 20 characters or less";
    }
    if (!/^[a-zA-Z0-9_]+$/.test(value)) {
      return "Only letters, numbers, and underscores allowed";
    }
    return null;
  };

  // Check username availability
  const checkAvailability = async (value: string) => {
    const validationError = validateUsername(value);
    if (validationError) {
      setError(validationError);
      setIsAvailable(null);
      return;
    }

    setIsChecking(true);
    setError(null);

    try {
      const snapshot = await dbGet(`v1/users/usernames/${value.toLowerCase()}`);
      if (snapshot.exists()) {
        setError("Username is already taken");
        setIsAvailable(false);
      } else {
        setError(null);
        setIsAvailable(true);
      }
    } catch (err) {
      console.error("Error checking username:", err);
      setError("Failed to check availability");
      setIsAvailable(null);
    } finally {
      setIsChecking(false);
    }
  };

  // Handle input change with debounced availability check
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9_]/g, "");
    setUsername(value);
    setIsAvailable(null);
    setError(null);

    // Debounce the availability check
    if (value.length >= 3) {
      const timeoutId = setTimeout(() => {
        checkAvailability(value);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  };

  // Handle photo file selection
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setError(null);
    setPhotoFile(file);
    setUseGooglePhoto(false);

    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Handle username form submission - go to photo step
  const handleUsernameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateUsername(username);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!isAvailable) {
      await checkAvailability(username);
      if (!isAvailable) return;
    }

    // Move to photo step
    setError(null);
    setStep('photo');
  };

  // Handle final submission with photo
  const handleFinalSubmit = async (skipPhoto: boolean = false) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Determine which photo to use
      let finalPhotoFile: File | null = null;
      if (!skipPhoto && photoFile) {
        finalPhotoFile = photoFile;
      } else if (!skipPhoto && useGooglePhoto && suggestedPhotoURL) {
        // User is keeping Google photo - no file to upload, but we'll pass null
        // and the parent component should use suggestedPhotoURL
        finalPhotoFile = null;
      }

      await onSubmit(username, finalPhotoFile);
    } catch (err: any) {
      setError(err.message || "Failed to complete setup");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get the display photo
  const displayPhoto = photoPreview || (useGooglePhoto ? suggestedPhotoURL : null) || DEFAULT_AVATAR;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative w-full max-w-md bg-[#0A0A0A] border border-primary/30 rounded-2xl overflow-hidden"
        >
          {/* Decorative background glow */}
          <div className="absolute inset-0 bg-gradient-to-b from-primary/20 via-transparent to-transparent pointer-events-none" />

          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handlePhotoSelect}
            accept="image/*"
            className="hidden"
          />

          {/* Content */}
          <div className="relative p-8">
            {/* Step indicator */}
            <div className="flex justify-center gap-2 mb-6">
              <div className={`w-2 h-2 rounded-full ${step === 'username' ? 'bg-primary' : 'bg-white/30'}`} />
              <div className={`w-2 h-2 rounded-full ${step === 'photo' ? 'bg-primary' : 'bg-white/30'}`} />
            </div>

            <AnimatePresence mode="wait">
              {step === 'username' ? (
                <motion.div
                  key="username-step"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  {/* Icon */}
                  <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                      <User className="w-8 h-8 text-primary" />
                    </div>
                  </div>

                  {/* Title */}
                  <h2 className="text-2xl font-black text-white text-center mb-2">
                    Choose Your Username
                  </h2>
                  <p className="text-white/60 text-center mb-8">
                    This is how other fans will know you on ParlayGround
                  </p>

                  {/* Form */}
                  <form onSubmit={handleUsernameSubmit}>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-white mb-2">
                        Username
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">
                          @
                        </span>
                        <input
                          type="text"
                          value={username}
                          onChange={handleChange}
                          placeholder="yourname"
                          maxLength={20}
                          className="w-full pl-10 pr-12 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                          autoFocus
                        />
                        {/* Status indicator */}
                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                          {isChecking && (
                            <Loader2 className="w-5 h-5 text-white/40 animate-spin" />
                          )}
                          {!isChecking && isAvailable === true && (
                            <Check className="w-5 h-5 text-green-500" />
                          )}
                          {!isChecking && isAvailable === false && (
                            <X className="w-5 h-5 text-red-500" />
                          )}
                        </div>
                      </div>

                      {/* Error message */}
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-center gap-2 mt-2 text-red-400 text-sm"
                        >
                          <AlertCircle className="w-4 h-4" />
                          {error}
                        </motion.div>
                      )}

                      {/* Success message */}
                      {isAvailable && !error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-center gap-2 mt-2 text-green-400 text-sm"
                        >
                          <Check className="w-4 h-4" />
                          Username is available!
                        </motion.div>
                      )}

                      {/* Character count */}
                      <div className="text-right text-white/30 text-xs mt-1">
                        {username.length}/20
                      </div>
                    </div>

                    {/* Submit button */}
                    <button
                      type="submit"
                      disabled={isChecking || !username || !!error}
                      className="w-full py-4 bg-primary text-black font-bold rounded-full shadow-neon hover:shadow-neon-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      Continue
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </form>
                </motion.div>
              ) : (
                <motion.div
                  key="photo-step"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  {/* Photo Preview */}
                  <div className="flex justify-center mb-6">
                    <div className="relative">
                      <img
                        src={displayPhoto}
                        alt="Profile"
                        className="w-24 h-24 rounded-full object-cover ring-4 ring-primary/20"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="absolute bottom-0 right-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center hover:bg-primary/90 transition-colors"
                      >
                        <Camera className="w-4 h-4 text-black" />
                      </button>
                    </div>
                  </div>

                  {/* Title */}
                  <h2 className="text-2xl font-black text-white text-center mb-2">
                    Add a Profile Picture
                  </h2>
                  <p className="text-white/60 text-center mb-6">
                    Help others recognize you on ParlayGround
                  </p>

                  {/* Error message */}
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center justify-center gap-2 mb-4 text-red-400 text-sm"
                    >
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </motion.div>
                  )}

                  {/* Photo options */}
                  <div className="space-y-3 mb-6">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full py-3 bg-white/10 hover:bg-white/15 border border-white/10 rounded-xl text-white font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <Upload className="w-5 h-5" />
                      {photoFile ? 'Choose Different Photo' : 'Upload Photo'}
                    </button>

                    {suggestedPhotoURL && !photoFile && (
                      <button
                        onClick={() => {
                          setUseGooglePhoto(true);
                          setPhotoPreview(suggestedPhotoURL);
                        }}
                        className={`w-full py-3 border rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
                          useGooglePhoto
                            ? 'bg-primary/20 border-primary text-primary'
                            : 'bg-white/5 border-white/10 text-white hover:bg-white/10'
                        }`}
                      >
                        <Check className={`w-5 h-5 ${useGooglePhoto ? 'opacity-100' : 'opacity-0'}`} />
                        Use Google Photo
                      </button>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="space-y-3">
                    <button
                      onClick={() => handleFinalSubmit(false)}
                      disabled={isSubmitting}
                      className="w-full py-4 bg-primary text-black font-bold rounded-full shadow-neon hover:shadow-neon-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Setting up...
                        </>
                      ) : (
                        <>
                          Complete Setup
                          <Check className="w-5 h-5" />
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => handleFinalSubmit(true)}
                      disabled={isSubmitting}
                      className="w-full py-3 text-white/60 hover:text-white font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <SkipForward className="w-4 h-4" />
                      Skip for now
                    </button>
                  </div>

                  <p className="text-center text-white/40 text-xs mt-4">
                    You can add or change your photo anytime
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UsernameSetupModal;
